/**
 * Payments frontend helpers for COURTPLAN.
 * - buyPackage(packageId): create a checkout session and redirect to Stripe.
 * - pollPaymentStatus(sessionId): poll the backend until paid/expired; activates credits/subscription on success.
 */
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export async function fetchPackages() {
  const { data } = await axios.get(`${API}/payments/packages`);
  return data.packages || [];
}

export async function buyPackage(packageId) {
  const { data } = await axios.post(
    `${API}/payments/checkout/session`,
    { package_id: packageId, origin_url: window.location.origin },
    { withCredentials: true }
  );
  if (data?.url) {
    window.location.href = data.url;
  } else {
    throw new Error('No checkout URL returned');
  }
}

/**
 * Poll /api/payments/checkout/status/{session_id} up to max attempts.
 * Returns: { status, payment_status, package_id } on paid, or throws.
 */
export async function pollPaymentStatus(sessionId, { maxAttempts = 10, interval = 2500 } = {}) {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const { data } = await axios.get(`${API}/payments/checkout/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === 'paid') return data;
      if (data.status === 'expired') {
        throw new Error('Η συνεδρία πληρωμής έληξε');
      }
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 404) throw e;
      // keep polling on transient errors
    }
    await new Promise((r) => setTimeout(r, interval));
  }
  throw new Error('Ο έλεγχος πληρωμής έληξε — ελέγξτε την συνδρομή σας σε λίγα λεπτά.');
}

/**
 * After returning from Stripe success redirect, run this once on mount to finalize.
 * Reads ?payment_session_id=... from the URL and polls.
 * On paid: fires toast and calls onRefresh().
 */
export async function handleStripeReturn(onRefresh) {
  const qs = new URLSearchParams(window.location.search);
  const cancelled = qs.get('payment_cancelled');
  const sid = qs.get('payment_session_id');
  if (cancelled) {
    toast.info('Η πληρωμή ακυρώθηκε.');
    cleanUrl();
    return;
  }
  if (!sid) return;
  try {
    toast.loading('Επεξεργασία πληρωμής...', { id: 'pay-proc' });
    const data = await pollPaymentStatus(sid);
    toast.dismiss('pay-proc');
    toast.success('Η πληρωμή ολοκληρώθηκε!', {
      description: 'Οι μονάδες ή η συνδρομή σας ενεργοποιήθηκαν.',
    });
    if (typeof onRefresh === 'function') await onRefresh();
  } catch (e) {
    toast.dismiss('pay-proc');
    toast.error(e?.response?.data?.detail || e?.message || 'Σφάλμα ελέγχου πληρωμής');
  } finally {
    cleanUrl();
  }
}

function cleanUrl() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('payment_session_id');
    url.searchParams.delete('payment_cancelled');
    window.history.replaceState({}, document.title, url.pathname + (url.search ? url.search : ''));
  } catch { /* noop */ }
}
